<template>
  <div
    id="reconciliation-approvals-list"
    :class="{ 'page-fullscreen': pageStore.isFullScreen }"
  >
    <v-card tile :class="$vuetify.breakpoint.mdAndDown ? null : 'rounded-t-xl'">
      <PageHeader />

      <div v-if="appStore.pendingMutabakat.length > 0" class="pending-mutabakat-box">
        <div class="pending-mutabakat-box__title warning--text">
          <v-icon color="warning" size="20" class="mr-2">{{ icons.mdiAlertCircleOutline }}</v-icon>
          Onayınızı bekleyen mutabakat bulunuyor
        </div>
        <div
          v-for="pending in appStore.pendingMutabakat"
          :key="pending.mutabakat_no"
          class="pending-mutabakat-row"
        >
          <div class="pending-mutabakat-row__info">
            <v-chip
              v-if="pending.must_approve"
              color="error"
              text-color="white"
              small
              class="font-weight-bold"
            >
              Süre Doldu
            </v-chip>
            <v-chip
              v-else-if="pending.remaining_days != null"
              :color="pending.remaining_days <= 0 ? 'error' : 'warning'"
              text-color="white"
              small
              class="font-weight-bold"
            >
              {{ pending.remaining_days <= 0 ? "Süre Doldu" : `${pending.remaining_days} Gün Kaldı` }}
            </v-chip>
            <div class="text-caption">
              <strong>{{ pending.mutabakat_no }}</strong> numaralı mutabakat gönderiminin üzerinden
              {{ pending.passed_days }} gün geçti.
              <span v-if="pending.must_approve" class="font-weight-bold error--text">
                Onaylama süresi geçmiştir.
              </span>
              <span v-else-if="pending.remaining_days != null" class="font-weight-bold">
                Onaylamak için son {{ pending.remaining_days }} gününüz kaldı.
              </span>
            </div>
          </div>
          <PendingReviewDialog :pending="pending" @responded="onPendingResponded" />
        </div>
      </div>

      <v-data-table
        :headers="tableColumns"
        :items="tableItems"
        :loading="tableLoading"
        :height="pageStore.tableHeight"
        loader-height="2"
        :mobile-breakpoint="100"
        fixed-header
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- Mutabakat No -->
        <template #[`item.mutabakat_no`]="{ item }">
          <v-chip label color="chip" class="d-flex justify-center">
            <div class="text-truncate text-uppercase data-table-item-type-one">
              {{ item.mutabakat_no }}
            </div>
          </v-chip>
        </template>

        <!-- Onay/Ret Durumu -->
        <template #[`item.status_text`]="{ item }">
          <v-chip
            :color="statusColor(item.status)"
            class="d-flex justify-center text-caption font-weight-bold"
            label
          >
            {{ item.status_text }}
          </v-chip>
        </template>

        <!-- Açıklama -->
        <template #[`item.note`]="{ item }">
          <div class="text-truncate" :title="item.note">
            {{ item.note || "-" }}
          </div>
        </template>

        <!-- Cevap Tarihi -->
        <template #[`item.updated_at`]="{ item }">
          {{ item.updated_at || "-" }}
        </template>

        <!-- Hareket -->
        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <PendingReviewDialog :pending="item" @responded="tableFetch" />
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue-demi";

// Pinia
import { usePageStore } from "@/stores/pageHeader";
import { useAppStore } from "@/stores/app";

// Utils
import { useToast } from "vue-toastification/composition";

// Api
import { getCustomerMutabakatApprovals } from "@/api/reconciliation";

// Components
import PageHeader from "@/components/PageHeader/Index.vue";
import PendingReviewDialog from "./PendingReviewDialog.vue";

// Icons
import { mdiFileDocumentOutline, mdiAlertCircleOutline } from "@mdi/js";

export default {
  components: {
    PageHeader,
    PendingReviewDialog,
  },
  setup() {
    const pageStore = usePageStore();
    const appStore = useAppStore();
    const toast = useToast();

    const pageSettings = () => {
      pageStore.pageTitle = "Mutabakat Onay Geçmişim";
      pageStore.pageIcon = mdiFileDocumentOutline;
      pageStore.hasFullScreen = false;
      pageStore.hasDownload = false;
      pageStore.hasDateRangeAll = false;
      pageStore.hasDateRange = false;
      pageStore.hasActions = false;
    };

    // Mutabakat Onay/Ret (bkz. PendingReviewDialog.vue)
    // Cevaplanan mutabakat (onay ya da ret) bir daha değiştirilemediği için
    // bekleyen kutudan kaldırılır. Sipariş engelinin devam etmesi ayrı bir
    // uyarı (need_approve) ile sipariş sayfalarında gösterilir.
    const onPendingResponded = ({ pending }) => {
      appStore.pendingMutabakat = appStore.pendingMutabakat.filter(
        (item) => item.mutabakat_no !== pending.mutabakat_no
      );

      tableFetch();
    };

    // Table
    const tableLoading = ref(true);
    const tableItems = ref([]);

    // Onay/Ret durumuna göre chip rengi
    const statusColor = (status) => {
      const map = {
        3: "success",
        4: "error",
      };
      return map[status] || "grey";
    };

    // Table Columns
    const tableColumns = ref([
      { text: "Mutabakat No", value: "mutabakat_no", sortable: false, width: "150px" },
      { text: "Durum", value: "status_text", sortable: false, width: "150px" },
      { text: "Açıklama", value: "note", sortable: false, width: "260px" },
      { text: "Cevap Tarihi", value: "updated_at", sortable: false, width: "170px" },
      { text: "", value: "actions", sortable: false, width: "90px" },
    ]);

    // API İstek
    const tableFetch = () => {
      tableLoading.value = true;

      getCustomerMutabakatApprovals()
        .then((response) => {
          if (response.error) {
            throw new Error(response.msg);
          }

          tableItems.value = (response.response.result || []).map((item) => ({
            id: item.id,
            mutabakat_no: item.mutabakat_no,
            status: item.status,
            status_text: item.STATUS_TEXT,
            note: item.note,
            pdf_url: item.pdf_url,
            username: item.username,
            created_at: item.created_at,
            updated_at: item.updated_at,
          }));
        })
        .catch((e) => {
          toast.error(e.message, {
            position: "bottom-center",
          });
        })
        .finally(() => {
          tableLoading.value = false;
        });
    };

    onMounted(() => {
      pageSettings();
      tableFetch();
    });

    onUnmounted(() => {
      pageStore.pageSettingsClear();
    });

    return {
      // Pinia
      pageStore,
      appStore,

      // Bekleyen Mutabakat
      onPendingResponded,

      // Table
      tableFetch,
      tableColumns,
      tableItems,
      tableLoading,
      statusColor,

      icons: {
        mdiAlertCircleOutline,
      },
    };
  },
};
</script>

<style>
#reconciliation-approvals-list table > thead > tr > th {
  white-space: nowrap;
}

#reconciliation-approvals-list table > tbody > tr > td,
#reconciliation-approvals-list table > thead > tr > th {
  padding: 0 8px !important;
  font-size: 12px !important;
}

#reconciliation-approvals-list table > tbody > tr > td {
  height: 44px !important;
}

#reconciliation-approvals-list .v-chip {
  font-size: 11px !important;
  height: 24px !important;
}

.pending-mutabakat-box {
  background-color: rgba(255, 180, 0, 0.14);
  border-left: 4px solid var(--v-warning-base, #ffb400);
  padding: 16px 24px;
}

.pending-mutabakat-box__title {
  display: flex;
  align-items: center;
  font-weight: 700;
  margin-bottom: 12px;
}

.pending-mutabakat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background-color: rgba(255, 180, 0, 0.08);
  border-radius: 12px;
  padding: 10px 16px;
  margin-bottom: 8px;
}

.pending-mutabakat-row:last-child {
  margin-bottom: 0;
}

.pending-mutabakat-row__info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
